/**
 * A duty & tax estimate (`POST /tradeCosts/estimate/`, docs/decisions/hs-duty-tax.md part 3).
 *
 * Always a range with its assumptions, warnings, sources and confidence —
 * never a single total ("estimate, not a customs ruling"). Each layer is a
 * bar on a shared scale: the solid part reaches the low figure, the lighter
 * part the high one, and the figures are written next to it.
 */
import { useTranslation } from "react-i18next";
import { formatCurrency, formatDate, formatNumber } from "../../i18n/format";
import { hsDepthKey } from "../../lib/hsCodes";
import type { EstimateLayer, EstimateResponse } from "../../services/tradecosts/generated/model";
import "./TradeEstimate.css";

const num = (v: string | null | undefined): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const CONFIDENCES = ["low", "medium", "high"] as const;
type Confidence = (typeof CONFIDENCES)[number];
const confidenceKey = (c: string): Confidence =>
  (CONFIDENCES as readonly string[]).includes(c) ? (c as Confidence) : "low";

const SOURCE_KINDS = ["tax", "duty", "agreement", "rule", "fx"] as const;
type SourceKind = (typeof SOURCE_KINDS)[number];
const sourceKindKey = (k: string): SourceKind =>
  (SOURCE_KINDS as readonly string[]).includes(k) ? (k as SourceKind) : "rule";

const basisKey = (b: string): "cif" | "transaction" | "unknown" => (b === "cif" || b === "transaction" ? b : "unknown");

const TradeEstimateResult = ({ estimate }: { estimate: EstimateResponse }) => {
  const { t } = useTranslation("trade");
  const cur = estimate.currency;
  const money = (v: string) => formatCurrency(v, cur, { maximumFractionDigits: 0 });
  const range = (low: string, high: string) =>
    num(low) === num(high) ? money(low) : t("estimate.range", { low: money(low), high: money(high) });
  const scale = Math.max(num(estimate.total.high), 1);
  const confidence = confidenceKey(estimate.confidence);

  return (
    <div className="te">
      <section className="te-card">
        <div className="te-hero">
          <div>
            <div className="te-stat__label">{t("estimate.total")}</div>
            <div className="te-stat__value">{range(estimate.total.low, estimate.total.high)}</div>
            <div className="te-stat__sub">
              {estimate.percentOfValue.low !== null && estimate.percentOfValue.high !== null
                ? t("estimate.percentOfValue", {
                    low: formatNumber(estimate.percentOfValue.low, { maximumFractionDigits: 1 }),
                    high: formatNumber(estimate.percentOfValue.high, { maximumFractionDigits: 1 }),
                  })
                : null}
            </div>
          </div>
          <div>
            <div className="te-stat__label">{t("estimate.net")}</div>
            <div className="te-stat__value">{range(estimate.netOfReclaim.low, estimate.netOfReclaim.high)}</div>
            <div className="te-stat__sub">{t("estimate.netHelp")}</div>
          </div>
          <div>
            <div className="te-stat__label">{t("estimate.confidenceLabel")}</div>
            <div className="te-stat__value">
              <span className={`te-confidence te-confidence--${confidence}`}>
                <i
                  className={
                    confidence === "high"
                      ? "fa-solid fa-circle-check"
                      : confidence === "medium"
                        ? "fa-solid fa-circle-half-stroke"
                        : "fa-regular fa-circle"
                  }
                />
                {t(`estimate.confidence.${confidence}`)}
              </span>
            </div>
            <div className="te-stat__sub">{estimate.confidenceReasons.join(" · ")}</div>
          </div>
        </div>
      </section>

      <section className="te-card">
        <h3 className="te-card__title">{t("estimate.layersTitle")}</h3>
        <p className="te-help">
          {t("estimate.layersHelp", {
            code: estimate.hsCode.code,
            depth: t(`depth.${hsDepthKey(estimate.hsCode.depth)}`),
            description: estimate.hsCode.description,
          })}
        </p>
        <p className="te-help">
          {t("estimate.customsValueLine", {
            range: range(estimate.customsValue.low, estimate.customsValue.high),
            basis: t(`estimate.basis.${basisKey(estimate.valuationBasis)}`),
          })}
        </p>
        <div className="te-legend" aria-hidden>
          <span>
            <span className="te-legend__swatch" style={{ background: "var(--te-low)" }} />
            {t("estimate.legendLow")}
          </span>
          <span>
            <span className="te-legend__swatch" style={{ background: "var(--te-high)" }} />
            {t("estimate.legendHigh")}
          </span>
        </div>
        <div className="te-bars">
          {estimate.layers.map((layer) => (
            <LayerBar
              key={`${layer.key}-${layer.label}`}
              layer={layer}
              scale={scale}
              text={range(layer.low, layer.high)}
            />
          ))}
          <RangeBar
            total
            label={t("estimate.total")}
            low={estimate.total.low}
            high={estimate.total.high}
            scale={scale}
            text={range(estimate.total.low, estimate.total.high)}
          />
        </div>
        <p className="te-help" style={{ marginTop: 8 }}>
          {t("estimate.scaleNote")}
        </p>
      </section>

      {estimate.warnings.length > 0 ? (
        <section className="te-card">
          <h3 className="te-card__title">{t("estimate.warningsTitle")}</h3>
          {estimate.warnings.map((w) => (
            <div key={w.code} className={`te-warning ${w.certainty === "applies" ? "te-warning--applies" : ""}`}>
              <strong>{w.title}</strong>{" "}
              <span>({w.certainty === "applies" ? t("estimate.applies") : t("estimate.mayApply")})</span>
              <div>{w.message}</div>
            </div>
          ))}
        </section>
      ) : null}

      <div className="te-grid2">
        <section className="te-card">
          <h3 className="te-card__title">{t("estimate.assumptionsTitle")}</h3>
          <ul className="te-list">
            {estimate.assumptions.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </section>
        <section className="te-card">
          <h3 className="te-card__title">{t("estimate.sourcesTitle")}</h3>
          <ul className="te-list">
            {estimate.sources.map((s) => (
              <li key={`${s.kind}-${s.label}`}>
                <span className="te-bar__rate" style={{ display: "inline" }}>
                  {t(`estimate.sourceKind.${sourceKindKey(s.kind)}`)}:
                </span>{" "}
                {s.url ? (
                  <a href={s.url} target="_blank" rel="noreferrer">
                    {s.label}
                  </a>
                ) : (
                  s.label
                )}{" "}
                <span className="te-bar__rate" style={{ display: "inline" }}>
                  {t("estimate.asOf", { date: formatDate(s.asOf) })}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <p className="te-disclaimer">
        <i className="fa-solid fa-scale-balanced" /> {estimate.disclaimer}
      </p>
    </div>
  );
};

const LayerBar = ({ layer, scale, text }: { layer: EstimateLayer; scale: number; text: string }) => {
  const { t } = useTranslation("trade");
  const rate =
    layer.rateLow !== null && layer.rateHigh !== null
      ? num(layer.rateLow) === num(layer.rateHigh)
        ? t("estimate.rate", { rate: formatNumber(layer.rateLow, { maximumFractionDigits: 2 }) })
        : t("estimate.rateRange", {
            low: formatNumber(layer.rateLow, { maximumFractionDigits: 2 }),
            high: formatNumber(layer.rateHigh, { maximumFractionDigits: 2 }),
          })
      : undefined;
  return (
    <RangeBar
      label={layer.label}
      rate={rate}
      low={layer.low}
      high={layer.high}
      scale={scale}
      text={text}
      note={layer.note}
    />
  );
};

const RangeBar = ({
  label,
  rate,
  low,
  high,
  scale,
  text,
  note,
  total = false,
}: {
  label: string;
  rate?: string;
  low: string;
  high: string;
  scale: number;
  text: string;
  note?: string;
  total?: boolean;
}) => {
  const lowPct = Math.min(100, (num(low) / scale) * 100);
  const highPct = Math.min(100, (num(high) / scale) * 100);
  const lowStyle = { width: `${lowPct}%` };
  // i18n-ignore-next-line (CSS lengths, not text)
  const spanStyle = { left: `${lowPct}%`, width: `${highPct - lowPct}%` };
  const hasSpan = highPct - lowPct > 0;
  return (
    <div className={`te-bar ${total ? "te-bar--total" : ""}`}>
      <div className="te-bar__label">
        {label}
        {rate ? <span className="te-bar__rate">{rate}</span> : null}
      </div>
      <div className="te-bar__track" title={text}>
        <div className="te-bar__low" style={lowStyle} />
        {hasSpan ? <div className="te-bar__span" style={spanStyle} /> : null}
      </div>
      <div className="te-bar__value">{text}</div>
      {note ? <div className="te-bar__note">{note}</div> : null}
    </div>
  );
};

export default TradeEstimateResult;
